import React from 'react';
import { Clock, CheckCircle, BookOpen } from 'lucide-react';
import Button from '../../common/Button';
import type { TrainingCourse } from '../../../types/training';
import { useTranslatedContent } from '../../../hooks/useTranslatedContent';

interface TrainingCourseCardProps {
  course: TrainingCourse;
}

const TrainingCourseCard: React.FC<TrainingCourseCardProps> = ({ course }) => {
  const { t } = useTranslatedContent('request');

  // Early return if course data is incomplete
  if (!course || !course.title || !course.topics) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full flex flex-col">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
        <div className="flex items-center">
          {course.icon && <course.icon className="h-8 w-8 text-white" />}
          <div className="ml-4">
            <h3 className="text-xl font-bold text-white">{course.title}</h3>
            <div className="flex items-center mt-2 text-blue-100">
              <Clock className="h-4 w-4 mr-2" />
              <span>{course.duration}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 flex-grow flex flex-col">
        <p className="text-gray-600">{course.description}</p>

        {course.prerequisites && course.prerequisites.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-900">Prerequisites</h4>
            <ul className="mt-2 list-disc list-inside text-gray-600 space-y-1">
              {course.prerequisites.map((prereq, index) => (
                <li key={index}>{prereq}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex-grow">
          <h4 className="font-semibold text-gray-900 flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
            Key Topics
          </h4>
          <ul className="mt-3 space-y-2">
            {course.topics.map((topic, index) => (
              <li key={index} className="flex items-start text-gray-600">
                <CheckCircle className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{topic}</span>
              </li>
            ))}
          </ul>
        </div>

        {course.includes && course.includes.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-900">Course Includes</h4>
            <ul className="mt-2 list-disc list-inside text-gray-600 space-y-1">
              {course.includes.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8">
          <Button 
            variant="primary" 
            className="w-full"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t('submit')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrainingCourseCard;